import { inject } from '@angular/core';
import { CompanyApi } from '@app/api/company.api';
import { CreateCompanyUserRequest, UserDto } from '@app/models';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';

interface InitialState {
  companyUsers: UserDto[];
  isLoading: boolean;
  isSubmitting: boolean;
}

const initialState: InitialState = {
  companyUsers: [],
  isLoading: false,
  isSubmitting: false,
};

export const CompanyUsersStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withMethods((store) => {
    const companyApi = inject(CompanyApi);

    const loadCompanyUsers = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(() => companyApi.getUsers()),
        tap((companyUsers) =>
          patchState(store, {
            companyUsers,
          }),
        ),
      ),
    );

    const addCompanyUser = rxMethod<{
      request: CreateCompanyUserRequest;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ request, onSubmit, onSuccess }) =>
          companyApi.createUser$(request).pipe(
            tap((createdUser) => {
              patchState(store, (state) => ({
                companyUsers: [createdUser, ...state.companyUsers],
                isSubmitting: false,
              }));

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const updateCompanyUser = rxMethod<{
      id: number;
      request: CreateCompanyUserRequest;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ id, request, onSubmit, onSuccess }) =>
          companyApi.updateUser$(id, request).pipe(
            catchError(() => {
              patchState(store, { isSubmitting: false });

              return EMPTY;
            }),
            tap((updatedUser) => {
              patchState(store, (state) => ({
                companyUsers: state.companyUsers.map((user) =>
                  user.id === updatedUser.id ? updatedUser : user,
                ),
                isSubmitting: false,
              }));

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const deleteCompanyUser = rxMethod<{
      id: number;
      onSubmit: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ id, onSubmit }) =>
          companyApi.removeUser$(id).pipe(
            tap(() => {
              patchState(store, (state) => ({
                companyUsers: state.companyUsers.filter(
                  (user) => user.id !== id,
                ),
                isSubmitting: false,
              }));

              onSubmit();
            }),
          ),
        ),
      ),
    );

    const clearCompanyUsers = () => patchState(store, initialState);

    return {
      loadCompanyUsers,
      addCompanyUser,
      updateCompanyUser,
      deleteCompanyUser,
      clearCompanyUsers,
    };
  }),
);
